import { type FormEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/Toast";
import { request } from "../../../lib/api";
import { coverFor, initials } from "../../../lib/helpers";
import type { CourseDetail, CourseLevel } from "../../../lib/types";

export function DetailsTab({ course }: { course: CourseDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(course.title);
  const [subtitle, setSubtitle] = useState(course.subtitle || "");
  const [description, setDescription] = useState(course.description || "");
  const [category, setCategory] = useState(course.category);
  const [level, setLevel] = useState<CourseLevel>(course.level);
  const [duration, setDuration] = useState<number | null>(course.duration_minutes);
  const [emc, setEmc] = useState<number | null>(course.emc_credits);

  const save = useMutation({
    mutationFn: () => request<CourseDetail>(`/courses/${course.id}`, {
      method: "PATCH",
      body: { title, subtitle: subtitle || null, description: description || null, category, level, duration_minutes: duration, emc_credits: emc },
    }),
    onSuccess: (updated) => {
      qc.setQueryData(["course", course.id], updated);
      qc.invalidateQueries({ queryKey: ["courses"] });
      toast("Detalii salvate", "success");
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <div className="grid-2">
      <div>
        <form className="card" onSubmit={onSubmit}>
          <h3>Informații curs</h3>
          <div className="form-group"><label>Titlu</label><input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="form-group"><label>Subtitlu</label><input className="form-control" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} /></div>
          <div className="form-group"><label>Descriere completă</label><textarea className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="grid-2">
            <div className="form-group"><label>Categorie</label><input className="form-control" value={category} onChange={(e) => setCategory(e.target.value)} /></div>
            <div className="form-group">
              <label>Nivel</label>
              <select className="form-control" value={level} onChange={(e) => setLevel(e.target.value as CourseLevel)}>
                <option value="beginner">Începător</option>
                <option value="intermediate">Intermediar</option>
                <option value="advanced">Avansat</option>
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Durată (minute)</label><input className="form-control" type="number" value={duration ?? ""} onChange={(e) => setDuration(e.target.value ? Number(e.target.value) : null)} /></div>
            <div className="form-group"><label>Credite EMC</label><input className="form-control" type="number" value={emc ?? ""} onChange={(e) => setEmc(e.target.value ? Number(e.target.value) : null)} /></div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>
            <i className="fa-solid fa-floppy-disk"></i> {save.isPending ? "Se salvează..." : "Salvează detaliile"}
          </button>
        </form>
      </div>
      <div>
        <div className="card">
          <h3>Imagine de copertă</h3>
          <div style={{ aspectRatio: "16/9", borderRadius: 16, background: coverFor(course), display: "flex", alignItems: "flex-end", padding: 20, color: "#fff" }}>
            <div>
              <div style={{ fontSize: ".78rem", opacity: .9, textTransform: "uppercase", letterSpacing: ".1em" }}>{course.category}</div>
              <strong style={{ fontSize: "1.2rem" }}>{course.title}</strong>
            </div>
          </div>
          <small className="muted" style={{ display: "block", marginTop: 10 }}>Copertă generată automat din culorile platformei. Upload custom — viitor.</small>
        </div>
        <div className="card mt-24">
          <h3>Instructor</h3>
          {course.instructor ? (
            <div className="user-cell" style={{ padding: 12, background: "var(--c-bg)", borderRadius: 10 }}>
              <div className="avatar">{initials(course.instructor.full_name)}</div>
              <div><strong>{course.instructor.full_name}</strong></div>
            </div>
          ) : (
            <div className="text-muted">Niciun instructor asignat.</div>
          )}
        </div>
      </div>
    </div>
  );
}
